<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import crmFormMixin from "@/mixins/crmForm.mixin";
import { leadFormMethods, clientGroupMethods, usersMethods, crmOptionMethods } from "@/state/helpers";
import { CRM_OPTION_MODULE } from "@/state/modules/crmOption";
import { required } from "vuelidate/lib/validators";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { sharedHelper, filtersHelper } from "@/utils";

/** Ready-made rows for the questions almost every form asks. */
const PRESETS = [
  { key: "contact_person", label: "Your name", type: "text", required: true,
    placeholder: "e.g. Tan Ah Kow", help: "So we know who to ask for." },
  { key: "phone", label: "Phone", type: "phone", required: true,
    placeholder: "01X-XXX XXXX", help: "We will call or WhatsApp this number." },
  { key: "email", label: "Email", type: "email", required: false,
    placeholder: "you@company.com", help: "We will send the quotation here." },
  { key: "company_name", label: "Company", type: "text", required: false,
    placeholder: "e.g. Bright Star Sdn Bhd", help: "Leave blank if this is personal." },
  { key: "notes", label: "What do you need?", type: "textarea", required: false,
    placeholder: "Tell us roughly what you are looking for", help: "The more detail, the faster we can quote." },
];

/** Types whose placeholder the public page actually draws — mirrors LeadForm::PLACEHOLDER_TYPES. */
const PLACEHOLDER_TYPES = ["text", "textarea", "email", "phone", "number", "select"];
const OPTION_TYPES = ["select", "radio", "checkbox"];

/**
 * The form builder.
 *
 * A form is a definition, so this page edits data rather than generating code:
 * whatever it saves, the public page at /forms/{uuid} renders. Field keys are the
 * load-bearing part — a key from the mapped list lands in a real lead column,
 * anything else is kept as a bespoke question, which is why the picker labels
 * the difference instead of leaving it to be discovered.
 */
export default {
  page() {
    return { title: this.isEdit ? i18n.t("leadForm.edit") : i18n.t("leadForm.create") };
  },
  components: { Layout, PageHeader, Multiselect },
  mixins: [crmFormMixin],
  data() {
    return {
      loading: true,
      tab: 0,
      form: {
        name: "",
        description: "",
        fields: [],
        default_source: "website",
        default_assigned_to: null,
        default_group_ids: [],
        submit_label: "Submit",
        success_message: "",
        redirect_url: "",
        is_active: true,
      },
      assignedUser: null,
      selectedGroups: [],
      embedCode: "",
      publicUrl: "",
      // Keyed exactly as the API reports them ("fields.2.help"), so a server
      // message and a local one land in the same place.
      rowErrors: {},
      viewing: null,
    };
  },
  validations: {
    form: {
      name: { required },
      fields: { required },
    },
  },
  computed: {
    isEdit() { return !!this.$route.params.uuid; },
    title() { return this.isEdit ? this.$t("leadForm.edit") : this.$t("leadForm.create"); },
    schema() { return this.$store.state.leadForm.schema || {}; },
    mappedKeys() { return this.schema.mapped_keys || []; },
    fieldTypes() {
      return (this.schema.field_types || []).map((t) => ({
        value: t,
        label: this.$t(`leadForm.type.${t}`),
      }));
    },
    sourceOptions() {
      return this.$store.getters[`${CRM_OPTION_MODULE}/group`]("lead_sources");
    },
    selectedSource() {
      return this.sourceOptions.find((s) => s.value === this.form.default_source) || null;
    },
    userOptions() {
      return (this.$store.state.users.list.data || []).map((u) => ({ uuid: u.id, name: u.name }));
    },
    groupOptions() {
      return (this.$store.state.clientGroup.list || []).map((g) => ({ id: g.id, name: g.name }));
    },
    submissions() { return this.$store.state.leadForm.submissions || []; },
    /**
     * Intake keys on phone or email. Without one, every submission collapses
     * onto the same nameless lead — the API refuses it, so warn here first.
     */
    missingIdentity() {
      return !this.form.fields.some((f) => f.key === "phone" || f.key === "email");
    },
    presets() { return PRESETS; },
  },
  watch: {
    // Fetched on open rather than up front: most edits never look at them.
    tab(value) {
      if (value === 2 && this.isEdit) this.loadSubmissions();
    },
  },
  async created() {
    await Promise.all([
      this.getLeadFormSchema(),
      this.getAllClientGroups(),
      this.getCrmOptions(),
      this.getAllUser({ filter: {} }),
    ]);

    if (this.isEdit) {
      const r = await this.getLeadForm({ uuid: this.$route.params.uuid });
      if (r && r.data) this.hydrate(r.data);
    } else {
      this.form.fields = JSON.parse(JSON.stringify(PRESETS));
    }

    this.loading = false;
  },
  methods: {
    ...leadFormMethods,
    ...clientGroupMethods,
    ...usersMethods,
    ...crmOptionMethods,

    hydrate(d) {
      this.form = {
        name: d.name || "",
        description: d.description || "",
        fields: (d.fields || []).map((f) => ({
          key: f.key,
          label: f.label,
          type: f.type,
          required: !!f.required,
          placeholder: f.placeholder || "",
          help: f.help || "",
          options: (f.options || []).slice(),
        })),
        default_source: d.default_source || "website",
        default_assigned_to: d.default_assigned_to || null,
        default_group_ids: d.default_group_ids || [],
        submit_label: d.submit_label || "Submit",
        success_message: d.success_message || "",
        redirect_url: d.redirect_url || "",
        is_active: !!d.is_active,
      };
      this.embedCode = d.embed_code || "";
      this.publicUrl = d.public_url || "";
      this.assignedUser = this.userOptions.find((u) => u.uuid === this.form.default_assigned_to) || null;
      this.selectedGroups = this.groupOptions.filter((g) => this.form.default_group_ids.includes(g.id));
    },

    addField() {
      this.form.fields.push({
        key: "", label: "", type: "text", required: false,
        placeholder: "", help: "", options: [],
      });
    },
    addPreset(preset) {
      if (this.form.fields.some((f) => f.key === preset.key)) return;
      this.form.fields.push({ ...preset, options: [] });
    },
    removeField(i) {
      this.form.fields.splice(i, 1);
      // Row errors are keyed by index, so they all shift; recompute rather than
      // leave a message pinned to whatever moved into that slot.
      this.rowErrors = {};
    },
    moveField(i, delta) {
      const to = i + delta;
      if (to < 0 || to >= this.form.fields.length) return;
      const [row] = this.form.fields.splice(i, 1);
      this.form.fields.splice(to, 0, row);
      this.rowErrors = {};
    },
    /** Label → key, but only while the key is untouched, so edits stick. */
    onLabelInput(field) {
      if (field._keyTouched) return;
      field.key = (field.label || "")
        .toLowerCase().trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 60);
    },
    onKeyInput(field) {
      field._keyTouched = true;
      field.key = (field.key || "").toLowerCase().replace(/[^a-z0-9_]/g, "");
    },
    setType(field, option) { field.type = option ? option.value : "text"; },
    /** Type a choice, press enter. Duplicates are ignored rather than refused. */
    addOption(field, i, raw) {
      const value = (raw || "").trim();
      if (!value || (field.options || []).includes(value)) return;
      field.options.push(value);
      this.clearRowError(i, "options");
    },
    setOptions(field, i, value) {
      field.options = value || [];
      this.clearRowError(i, "options");
    },
    typeOf(field) { return this.fieldTypes.find((t) => t.value === field.type) || null; },
    needsOptions(field) { return OPTION_TYPES.includes(field.type); },
    hasPlaceholder(field) { return PLACEHOLDER_TYPES.includes(field.type); },
    isMapped(field) { return this.mappedKeys.includes(field.key); },

    /** A row's error, whether it came from here or from the API. */
    rowError(i, attr) {
      return this.rowErrors[`fields.${i}.${attr}`] || null;
    },
    hasRowError(i) {
      return Object.keys(this.rowErrors).some((k) => k.startsWith(`fields.${i}.`));
    },
    /** Clears one row error as soon as the user starts fixing it. */
    clearRowError(i, attr) {
      const key = `fields.${i}.${attr}`;
      if (this.rowErrors[key]) this.$delete(this.rowErrors, key);
    },
    /**
     * Checks every question before asking the server.
     *
     * The API enforces all of this too — this exists so the message lands on the
     * row that caused it instead of arriving as one toast about "fields.2.help".
     */
    validateRows() {
      const errors = {};
      const seen = {};

      this.form.fields.forEach((f, i) => {
        if (!f.label || !f.label.trim()) {
          errors[`fields.${i}.label`] = this.$t("leadForm.errLabel");
        }
        if (!f.key || !f.key.trim()) {
          errors[`fields.${i}.key`] = this.$t("leadForm.errKey");
        } else if (seen[f.key] !== undefined) {
          errors[`fields.${i}.key`] = this.$t("leadForm.errDuplicateKey", [seen[f.key] + 1]);
        } else {
          seen[f.key] = i;
        }
        if (!f.help || !f.help.trim()) {
          errors[`fields.${i}.help`] = this.$t("leadForm.errHelp");
        }
        if (this.hasPlaceholder(f) && (!f.placeholder || !f.placeholder.trim())) {
          errors[`fields.${i}.placeholder`] = this.$t("leadForm.errPlaceholder");
        }
        if (this.needsOptions(f) && (f.options || []).length < 2) {
          errors[`fields.${i}.options`] = this.$t("leadForm.errOptions");
        }
      });

      return errors;
    },
    scrollToFirstRowError() {
      this.$nextTick(() => {
        const el = this.$el.querySelector(".row-error");
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },
    setSource(o) { this.form.default_source = o ? o.value : null; },
    setUser(u) { this.assignedUser = u; this.form.default_assigned_to = u ? u.uuid : null; },
    setGroups(g) {
      this.selectedGroups = g || [];
      this.form.default_group_ids = this.selectedGroups.map((x) => x.id);
    },

    payload() {
      return {
        ...this.form,
        fields: this.form.fields.map((f) => ({
          key: f.key,
          label: f.label,
          type: f.type,
          required: !!f.required,
          placeholder: this.hasPlaceholder(f) ? f.placeholder || null : null,
          help: f.help || null,
          options: this.needsOptions(f) ? (f.options || []) : [],
        })),
      };
    },

    async save() {
      this.rowErrors = this.validateRows();
      if (Object.keys(this.rowErrors).length) {
        this.scrollToFirstRowError();
        return;
      }
      if (this.missingIdentity) {
        sharedHelper.alertErrorMessage(this.$t("leadForm.needIdentity"));
        return;
      }

      const data = this.payload();
      const result = await this.submitForm(
        () => (this.isEdit
          ? this.updateLeadForm({ uuid: this.$route.params.uuid, data })
          : this.createLeadForm({ data })),
        { errorContext: () => this.$store.state.leadForm.lastError }
      );

      // Anything the server rejected that we did not catch locally still has to
      // reach the right row rather than only a toast.
      if (!result) {
        const fromServer = {};
        Object.keys(this.serverErrors || {}).forEach((key) => {
          if (key.startsWith("fields.")) fromServer[key] = this.serverErrors[key];
        });
        if (Object.keys(fromServer).length) {
          this.rowErrors = fromServer;
          this.scrollToFirstRowError();
        }
      }

      // Only leave the page once the API confirmed it — otherwise the user
      // loses everything they typed on a validation failure.
      if (result) this.$router.push({ name: "Lead-form-listing" });
    },
    cancel() { this.$router.push({ name: "Lead-form-listing" }); },

    async copyEmbed() {
      try {
        await navigator.clipboard.writeText(this.embedCode);
        sharedHelper.alertSuccessMessage(this.$t("leadForm.embedCopied"));
      } catch (e) {
        window.prompt(this.$t("leadForm.copyManually"), this.embedCode);
      }
    },
    async loadSubmissions() {
      await this.getLeadFormSubmissions({ uuid: this.$route.params.uuid, params: { size: 50 } });
    },
    async rotate() {
      const ok = await sharedHelper.confirmDestructive({
        title: this.$t("leadForm.rotateTitle"),
        text: this.$t("leadForm.rotateText"),
        confirmText: this.$t("leadForm.rotate"),
      });
      if (!ok) return;
      await this.rotateLeadFormToken({ uuid: this.$route.params.uuid });
    },
    when(v) { return filtersHelper.getDateTimeFormat(v); },
    view(submission) {
      this.viewing = submission;
      this.$bvModal.show("submission-detail");
    },
    /** The submission's answers as label/value rows, in the form's own order. */
    viewingRows() {
      const payload = (this.viewing && this.viewing.payload) || {};
      const labelOf = {};
      this.form.fields.forEach((f) => { labelOf[f.key] = f.label; });

      // The form's questions first, then anything the payload has that the form
      // no longer asks — a question deleted after the fact still has answers.
      const ordered = this.form.fields.map((f) => f.key).filter((k) => k in payload);
      const orphans = Object.keys(payload).filter((k) => !ordered.includes(k));

      return ordered.concat(orphans).map((key) => ({
        key,
        label: labelOf[key] || key,
        known: key in labelOf,
        value: Array.isArray(payload[key]) ? payload[key].join(", ") : payload[key],
      }));
    },
    goLead(uuid) {
      this.$router.push({ name: "Lead-detail", params: { uuid } });
    },
    summarise(payload) {
      return Object.keys(payload || {})
        .map((k) => `${k}: ${Array.isArray(payload[k]) ? payload[k].join(", ") : payload[k]}`)
        .join(" · ") || "—";
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <div v-if="loading" class="text-center py-5">
      <b-spinner small variant="primary"></b-spinner>
    </div>

    <div v-else class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-tabs v-if="isEdit" v-model="tab" nav-class="mb-3">
              <b-tab :title="$t('leadForm.tabBuild')"></b-tab>
              <b-tab :title="$t('leadForm.tabShare')"></b-tab>
              <b-tab :title="$t('leadForm.tabSubmissions')"></b-tab>
            </b-tabs>

            <!-- ============ BUILD ============ -->
            <div v-show="!isEdit || tab === 0">
              <div class="row">
                <div class="col-12 mb-3">
                  <label>{{ $t('leadForm.name') }} <span class="text-danger">*</span></label>
                  <b-form-input
                    v-model="form.name"
                    :class="{ 'is-invalid': isInvalid('name') }"
                    :placeholder="$t('leadForm.namePlaceholder')"
                    @input="onFieldInput('name')"
                  />
                  <div v-if="fieldError('name', $t('leadForm.name'))" class="invalid-feedback d-block">
                    {{ fieldError('name', $t('leadForm.name')) }}
                  </div>
                </div>

                <div class="col-12 mb-3">
                  <label>{{ $t('leadForm.description') }}</label>
                  <b-form-textarea v-model="form.description" rows="2"
                                   :placeholder="$t('leadForm.descriptionPlaceholder')" />
                </div>
              </div>

              <hr />

              <div class="d-flex align-items-center mb-2">
                <h5 class="mb-0">{{ $t('leadForm.questions') }}</h5>
                <span class="flex-grow-1"></span>
                <b-button size="sm" variant="outline-primary" @click="addField">
                  <i class="mdi mdi-plus mr-1"></i>{{ $t('leadForm.addField') }}
                </b-button>
              </div>

              <div class="preset-row mb-3">
                <span class="text-muted small mr-2">{{ $t('leadForm.quickAdd') }}:</span>
                <b-button v-for="p in presets" :key="p.key"
                  size="sm" variant="light" class="mr-1 mb-1" @click="addPreset(p)">
                  + {{ p.label }}
                </b-button>
              </div>

              <b-alert :show="missingIdentity" variant="warning" class="py-2">
                <i class="mdi mdi-alert-outline mr-1"></i>{{ $t('leadForm.needIdentity') }}
              </b-alert>

              <div v-if="!form.fields.length" class="text-center py-4 text-muted">
                {{ $t('leadForm.noFields') }}
              </div>

              <div v-for="(field, i) in form.fields" :key="i"
                   class="field-card" :class="{ 'field-card-bad': hasRowError(i) }">
                <div class="field-head">
                  <span class="field-num">{{ i + 1 }}</span>
                  <span class="badge" :class="isMapped(field) ? 'badge-soft-primary' : 'badge-soft-secondary'">
                    {{ isMapped(field) ? $t('leadForm.mapped') : $t('leadForm.custom') }}
                  </span>
                  <span class="flex-grow-1"></span>
                  <a href="javascript:void(0);" class="mr-2 text-muted" @click="moveField(i, -1)">
                    <i class="mdi mdi-arrow-up"></i>
                  </a>
                  <a href="javascript:void(0);" class="mr-2 text-muted" @click="moveField(i, 1)">
                    <i class="mdi mdi-arrow-down"></i>
                  </a>
                  <a href="javascript:void(0);" class="text-danger" @click="removeField(i)">
                    <i class="mdi mdi-delete"></i>
                  </a>
                </div>

                <div class="row">
                  <div class="col-12 mb-2">
                    <label class="small">{{ $t('leadForm.fieldLabel') }} <span class="text-danger">*</span></label>
                    <b-form-input v-model="field.label" size="sm"
                                  :class="{ 'is-invalid': rowError(i, 'label') }"
                                  :placeholder="$t('leadForm.fieldLabelPlaceholder')"
                                  @input="onLabelInput(field); clearRowError(i, 'label')" />
                    <div v-if="rowError(i, 'label')" class="invalid-feedback d-block row-error">
                      {{ rowError(i, 'label') }}
                    </div>
                  </div>
                  <div class="col-12 mb-2">
                    <label class="small">{{ $t('leadForm.fieldKey') }} <span class="text-danger">*</span></label>
                    <b-form-input v-model="field.key" size="sm" placeholder="phone"
                                  :class="{ 'is-invalid': rowError(i, 'key') }"
                                  @input="onKeyInput(field); clearRowError(i, 'key')" />
                    <small class="text-muted">
                      {{ isMapped(field) ? $t('leadForm.mappedHelp', [field.key]) : $t('leadForm.customHelp') }}
                    </small>
                    <div v-if="rowError(i, 'key')" class="invalid-feedback d-block row-error">
                      {{ rowError(i, 'key') }}
                    </div>
                  </div>
                  <div class="col-12 mb-2">
                    <label class="small">{{ $t('leadForm.fieldType') }}</label>
                    <multiselect
                      :value="typeOf(field)"
                      :options="fieldTypes"
                      track-by="value"
                      label="label"
                      :show-labels="false"
                      :allow-empty="false"
                      open-direction="below"
                      :placeholder="$t('label.select')"
                      @input="setType(field, $event)"
                    />
                  </div>
                  <div v-if="needsOptions(field)" class="col-12 mb-2">
                    <label class="small">{{ $t('leadForm.fieldOptions') }} <span class="text-danger">*</span></label>
                    <multiselect
                      :value="field.options"
                      :options="field.options"
                      multiple
                      taggable
                      :show-labels="false"
                      open-direction="below"
                      :class="{ 'ms-invalid': rowError(i, 'options') }"
                      :placeholder="$t('leadForm.fieldOptionsPlaceholder')"
                      :tag-placeholder="$t('leadForm.fieldOptionsTagHint')"
                      @tag="addOption(field, i, $event)"
                      @input="setOptions(field, i, $event)"
                    />
                    <small class="text-muted">{{ $t('leadForm.fieldOptionsHelp') }}</small>
                    <div v-if="rowError(i, 'options')" class="invalid-feedback d-block row-error">
                      {{ rowError(i, 'options') }}
                    </div>
                  </div>
                  <!-- Radio, checkbox and date have nowhere to show a placeholder,
                       so the input is hidden rather than asked for and ignored. -->
                  <div v-if="hasPlaceholder(field)" class="col-12 mb-2">
                    <label class="small">{{ $t('leadForm.fieldPlaceholder') }} <span class="text-danger">*</span></label>
                    <b-form-input v-model="field.placeholder" size="sm"
                                  :class="{ 'is-invalid': rowError(i, 'placeholder') }"
                                  @input="clearRowError(i, 'placeholder')" />
                    <small class="text-muted">{{ $t('leadForm.fieldPlaceholderHelp') }}</small>
                    <div v-if="rowError(i, 'placeholder')" class="invalid-feedback d-block row-error">
                      {{ rowError(i, 'placeholder') }}
                    </div>
                  </div>
                  <div class="col-12 mb-2">
                    <label class="small">{{ $t('leadForm.fieldHelp') }} <span class="text-danger">*</span></label>
                    <b-form-input v-model="field.help" size="sm"
                                  :class="{ 'is-invalid': rowError(i, 'help') }"
                                  @input="clearRowError(i, 'help')" />
                    <small class="text-muted">{{ $t('leadForm.fieldHelpHelp') }}</small>
                    <div v-if="rowError(i, 'help')" class="invalid-feedback d-block row-error">
                      {{ rowError(i, 'help') }}
                    </div>
                  </div>
                  <div class="col-12">
                    <b-form-checkbox v-model="field.required" switch>
                      {{ $t('leadForm.fieldRequired') }}
                    </b-form-checkbox>
                  </div>
                </div>
              </div>

              <hr />

              <h5 class="mb-3">{{ $t('leadForm.whatHappensNext') }}</h5>
              <div class="row">
                <div class="col-12 mb-3">
                  <label>{{ $t('leadForm.defaultSource') }}</label>
                  <multiselect
                    :value="selectedSource"
                    :options="sourceOptions"
                    track-by="value"
                    label="label"
                    :show-labels="false"
                    open-direction="below"
                    :placeholder="$t('label.select')"
                    @input="setSource"
                  />
                </div>
                <div class="col-12 mb-3">
                  <label>{{ $t('leadForm.defaultAssignee') }}</label>
                  <multiselect
                    :value="assignedUser"
                    :options="userOptions"
                    track-by="uuid"
                    label="name"
                    :show-labels="false"
                    open-direction="below"
                    :placeholder="$t('leadForm.noAssignee')"
                    @input="setUser"
                  />
                  <small class="text-muted">{{ $t('leadForm.defaultAssigneeHelp') }}</small>
                </div>
                <div class="col-12 mb-3">
                  <label>{{ $t('leadForm.defaultGroups') }}</label>
                  <multiselect
                    :value="selectedGroups"
                    :options="groupOptions"
                    track-by="id"
                    label="name"
                    multiple
                    :show-labels="false"
                    open-direction="below"
                    :placeholder="$t('leadForm.noGroups')"
                    @input="setGroups"
                  />
                  <small class="text-muted">{{ $t('leadForm.defaultGroupsHelp') }}</small>
                </div>
                <div class="col-12 mb-3">
                  <label>{{ $t('leadForm.submitLabel') }}</label>
                  <b-form-input v-model="form.submit_label" placeholder="Submit" />
                </div>
                <div class="col-12 mb-3">
                  <label>{{ $t('leadForm.successMessage') }}</label>
                  <b-form-textarea v-model="form.success_message" rows="2"
                                   :placeholder="$t('leadForm.successMessagePlaceholder')" />
                </div>
                <div class="col-12 mb-3">
                  <label>{{ $t('leadForm.redirectUrl') }}</label>
                  <b-form-input v-model="form.redirect_url"
                                :class="{ 'is-invalid': isInvalid('redirect_url') }"
                                placeholder="https://example.com/thank-you"
                                @input="onFieldInput('redirect_url')" />
                  <small class="text-muted">{{ $t('leadForm.redirectUrlHelp') }}</small>
                  <div v-if="fieldError('redirect_url', $t('leadForm.redirectUrl'))" class="invalid-feedback d-block">
                    {{ fieldError('redirect_url', $t('leadForm.redirectUrl')) }}
                  </div>
                </div>
                <div class="col-12 mb-3">
                  <b-form-checkbox v-model="form.is_active" switch>
                    {{ $t('leadForm.isActive') }}
                  </b-form-checkbox>
                  <small class="text-muted">{{ $t('leadForm.isActiveHelp') }}</small>
                </div>
              </div>

              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button variant="primary" :disabled="submitting" @click="save">
                  <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
                  {{ isEdit ? $t('label.updateX', [$t('leadForm.name')]) : $t('label.submit') }}
                </b-button>
              </div>
            </div>

            <!-- ============ SHARE ============ -->
            <div v-show="isEdit && tab === 1">
              <div class="row">
                <div class="col-12 mb-3">
                  <label>{{ $t('leadForm.publicLink') }}</label>
                  <b-form-input :value="publicUrl" readonly />
                  <small class="text-muted">{{ $t('leadForm.publicLinkHelp') }}</small>
                </div>
                <div class="col-12 mb-3">
                  <label>{{ $t('leadForm.embedCode') }}</label>
                  <b-form-textarea :value="embedCode" rows="4" readonly class="code-box" />
                  <b-button size="sm" variant="outline-primary" class="mt-2" @click="copyEmbed">
                    <i class="mdi mdi-content-copy mr-1"></i>{{ $t('leadForm.copyEmbed') }}
                  </b-button>
                </div>
                <div class="col-12">
                  <hr />
                  <h6>{{ $t('leadForm.rotateTitle') }}</h6>
                  <p class="text-muted small">{{ $t('leadForm.rotateHelp') }}</p>
                  <b-button size="sm" variant="outline-danger" @click="rotate">
                    <i class="mdi mdi-key-change mr-1"></i>{{ $t('leadForm.rotate') }}
                  </b-button>
                </div>
              </div>
            </div>

            <!-- ============ SUBMISSIONS ============ -->
            <div v-show="isEdit && tab === 2">
              <p class="text-muted small">{{ $t('leadForm.submissionsHelp') }}</p>
              <div v-if="!submissions.length" class="text-center py-4 text-muted">
                {{ $t('leadForm.noSubmissions') }}
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>{{ $t('leadForm.when') }}</th>
                      <th>{{ $t('leadForm.status') }}</th>
                      <th>{{ $t('leadForm.answers') }}</th>
                      <th class="text-right">{{ $t('label.action') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="s in submissions" :key="s.uuid">
                      <td class="text-nowrap">{{ when(s.created_at) }}</td>
                      <td>
                        <span class="badge" :class="{
                          'badge-soft-success': s.status === 'accepted',
                          'badge-soft-warning': s.status === 'rejected',
                          'badge-soft-danger': s.status === 'spam' }">
                          {{ s.status }}
                        </span>
                        <div v-if="s.rejection_reason" class="text-muted small">{{ s.rejection_reason }}</div>
                      </td>
                      <td class="small summary-cell">{{ summarise(s.payload) }}</td>
                      <td class="text-right text-nowrap">
                        <a href="javascript:void(0);" class="text-info" @click="view(s)"
                           v-b-tooltip.hover :title="$t('leadForm.viewSubmission')">
                          <i class="mdi mdi-eye"></i>
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="submission-detail" :title="$t('leadForm.submissionDetail')" size="lg" hide-footer>
      <div v-if="viewing">
        <div class="d-flex align-items-center mb-3">
          <span class="badge mr-2" :class="{
            'badge-soft-success': viewing.status === 'accepted',
            'badge-soft-warning': viewing.status === 'rejected',
            'badge-soft-danger': viewing.status === 'spam' }">
            {{ viewing.status }}
          </span>
          <span class="text-muted small">{{ when(viewing.created_at) }}</span>
          <span class="flex-grow-1"></span>
          <b-button v-if="viewing.lead_uuid" size="sm" variant="outline-primary"
                    @click="goLead(viewing.lead_uuid)">
            <i class="mdi mdi-account-arrow-right mr-1"></i>{{ $t('leadForm.openLead') }}
          </b-button>
        </div>

        <b-alert :show="!!viewing.rejection_reason" variant="warning" class="py-2">
          <i class="mdi mdi-alert-outline mr-1"></i>
          {{ $t('leadForm.rejectedBecause') }}: {{ viewing.rejection_reason }}
        </b-alert>

        <table class="table table-sm mb-3">
          <tbody>
            <tr v-for="row in viewingRows()" :key="row.key">
              <th class="answer-key">
                {{ row.label }}
                <!-- The question was removed after this was submitted; the answer
                     is still worth showing rather than silently dropping. -->
                <b-badge v-if="!row.known" variant="light" class="ml-1">
                  {{ $t('leadForm.removedQuestion') }}
                </b-badge>
              </th>
              <td>{{ row.value || '—' }}</td>
            </tr>
            <tr v-if="!viewingRows().length">
              <td colspan="2" class="text-muted text-center">{{ $t('leadForm.noAnswers') }}</td>
            </tr>
          </tbody>
        </table>

        <div class="text-muted small">
          <div v-if="viewing.ip_address"><strong>IP:</strong> {{ viewing.ip_address }}</div>
          <div v-if="viewing.referrer"><strong>{{ $t('leadForm.referrer') }}:</strong> {{ viewing.referrer }}</div>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
.field-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
  background: #fcfcfd;
}
.field-card-bad { border-color: #e23d28; background: #fffafa; }
/* Same red border the other CRM forms give an invalid multiselect. */
.ms-invalid >>> .multiselect__tags { border-color: #e23d28; }
/* Same red border the other CRM forms give an invalid multiselect. */
.ms-invalid >>> .multiselect__tags { border-color: #e23d28; }
/* Same red border the other CRM forms give an invalid multiselect. */
.ms-invalid >>> .multiselect__tags { border-color: #e23d28; }
.field-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.field-num {
  width: 22px; height: 22px; border-radius: 50%;
  background: #7c4dff; color: #fff; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.preset-row { display: flex; align-items: center; flex-wrap: wrap; }
.code-box { font-family: monospace; font-size: 12px; }
.summary-cell { max-width: 420px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.answer-key { width: 34%; font-weight: 600; color: #495057; }
</style>
